import React, { useEffect, useRef, useState, useCallback } from "react";
import { IoCamera } from "react-icons/io5";
import { MdVideoLibrary } from "react-icons/md";
import { LuGalleryVerticalEnd } from "react-icons/lu";
import { FiCamera } from "react-icons/fi";
import ReactModal from "react-modal";
import Webcam from "react-webcam";
import { FaStop, FaPlay, FaPause, FaTrash, FaCheck } from "react-icons/fa";
import VideoLibraryModal from "../TWG/VideoLibraryModal";
import { IoMdClose } from "react-icons/io";

const UploadVideoButton = ({
  showVideoLibrarySelection = false,
  handleLibraryVideoSelection,
  handleVideoUpload,
  handleVideoCapture,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [showVideoLibraryModal, setShowVideoLibraryModal] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);

  // Video recording state
  const webcamRef = useRef(null);
  const inputRef = useRef();
  const [capturing, setCapturing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [videoRecorded, setVideoRecorded] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoCaption, setVideoCaption] = useState("");
  const [videoPlayer, setVideoPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const [videoTimer, setVideoTimer] = useState(0);

  // For picked video
  const [pickedVideoUrl, setPickedVideoUrl] = useState(null);
  const [pickedVideoFile, setPickedVideoFile] = useState(null);
  const [pickedVideoCaption, setPickedVideoCaption] = useState("");

  // Thumbnail state
  const [videoThumbnail, setVideoThumbnail] = useState(null); // Data URL
  const [videoThumbnailFile, setVideoThumbnailFile] = useState(null);

  // Format time as mm:ss
  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // --- VIDEO RECORDING ---
  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    setIsPaused(false);
    setRecordedChunks([]);
    setVideoRecorded(false);
    setVideoUrl(null);
    setVideoTimer(0);
    setVideoCaption("");
    mediaRecorderRef.current = new window.MediaRecorder(
      webcamRef.current.stream,
      {
        mimeType: "video/webm",
      },
    );
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable,
    );
    mediaRecorderRef.current.start();
    timerRef.current = setInterval(() => {
      setVideoTimer((prev) => prev + 1);
    }, 1000);
  }, [webcamRef, setCapturing, mediaRecorderRef]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => prev.concat(data));
    }
  }, []);

  const handlePauseCapture = () => {
    if (mediaRecorderRef.current && capturing && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
    }
  };

  const handleResumeCapture = () => {
    if (mediaRecorderRef.current && capturing && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setVideoTimer((prev) => prev + 1);
      }, 1000);
    }
  };

  const handleStopCaptureClick = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setCapturing(false);
      setIsPaused(false);
      setVideoRecorded(true);
      clearInterval(timerRef.current);
    }
  }, [mediaRecorderRef, setCapturing]);

  const handleDiscardVideo = () => {
    setCapturing(false);
    setIsPaused(false);
    setRecordedChunks([]);
    setVideoRecorded(false);
    setVideoUrl(null);
    setVideoTimer(0);
    setVideoCaption("");
    setIsPlaying(false);
    if (videoPlayer) {
      videoPlayer.pause();
      videoPlayer.currentTime = 0;
    }
  };

  // When video is recorded, create URL
  useEffect(() => {
    if (videoRecorded && recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    }
  }, [videoRecorded, recordedChunks]);

  // --- VIDEO SAVE ---
  const handleVideoSave = () => {
    if (recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, {
        type: "video/webm",
      });
      const file = new File([blob], "react-webcam-stream-capture.mp4", {
        type: "video/mp4",
      });
      handleVideoCapture(
        file,
        videoCaption,
        videoThumbnailFile, // Pass thumbnail file as 3rd argument if your handler supports it
      );
      handleDiscardVideo();
      setCameraModalOpen(false);
      setVideoThumbnail(null);
      setVideoThumbnailFile(null);
    }
  };

  // Dropdown close on outside click
  const containerRef = useRef();
  useEffect(() => {
    const eventHandler = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e.target)) {
        setIsActive(false);
      }
    };
    document.addEventListener("mouseup", eventHandler);
    return () => document.removeEventListener("mouseup", eventHandler);
  }, []);

  const handleVideoSelection = (details) => {
    if (handleLibraryVideoSelection) handleLibraryVideoSelection(details);
    setShowVideoLibraryModal(false);
  };

  // Handler to pick thumbnail from file
  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setVideoThumbnail(reader.result);
        setVideoThumbnailFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler to capture thumbnail from current video timestamp
  const handleCaptureThumbnail = (videoElement) => {
    if (!videoElement) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], "thumbnail.jpg", {
            type: "image/jpeg",
          });
          setVideoThumbnail(URL.createObjectURL(blob));
          setVideoThumbnailFile(file);
        }
      },
      "image/jpeg",
      0.95,
    );
  };

  // --- PICKED VIDEO PREVIEW & CAPTION ---
  const handleFileChange = (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("video/")) {
        setPickedVideoFile(file);
        setPickedVideoUrl(URL.createObjectURL(file));
        setPickedVideoCaption("");
        setVideoThumbnail(null);
        setVideoThumbnailFile(null);
      }
    }
  };

  const handlePickedVideoSave = () => {
    if (pickedVideoFile) {
      handleVideoCapture(
        pickedVideoFile,
        pickedVideoCaption,
        videoThumbnailFile, // Pass thumbnail file as 3rd argument if your handler supports it
      );
      setPickedVideoFile(null);
      setPickedVideoUrl(null);
      setPickedVideoCaption("");
      setVideoThumbnail(null);
      setVideoThumbnailFile(null);
      setIsActive(false);
    }
  };

  const handlePickedVideoDiscard = () => {
    setPickedVideoFile(null);
    setPickedVideoUrl(null);
    setPickedVideoCaption("");
    setVideoThumbnail(null);
    setVideoThumbnailFile(null);
    setIsActive(false);
  };

  return (
    <>
      <div className="relative">
        <button
          className="rounded-xl border border-white/30 px-4 py-2 text-white"
          onClick={() => setIsActive(true)}
        >
          <FiCamera className="text-xl" />
        </button>
        <input
          ref={inputRef}
          className="hidden"
          type="file"
          accept="video/*"
          multiple={false}
          onChange={handleFileChange}
        />
        <ul
          className={`${
            isActive ? "flex flex-col gap-1" : "hidden"
          } absolute -top-2 z-50 -translate-y-full text-xs transition-all duration-300`}
          ref={containerRef}
          onClick={() => setIsActive(false)}
        >
          {showVideoLibrarySelection && (
            <li
              onClick={() => setShowVideoLibraryModal(true)}
              className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-yellowish px-2.5 py-2 text-blackishV2"
            >
              <MdVideoLibrary className="text-base" />
              Select from library
            </li>
          )}
          <li
            onClick={() => inputRef.current.click()}
            className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-yellowish px-2.5 py-2 text-blackishV2"
          >
            <LuGalleryVerticalEnd className="text-base" />
            Select from Files
          </li>
          <li
            onClick={() => setCameraModalOpen(true)}
            className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-yellowish px-2.5 py-2 text-blackishV2"
          >
            <IoCamera className="text-base" />
            Open Camera
          </li>
        </ul>
      </div>
      {showVideoLibrarySelection && (
        <VideoLibraryModal
          showVideoLibraryModal={showVideoLibraryModal}
          setShowVideoLibraryModal={setShowVideoLibraryModal}
          handleVideoSelection={handleVideoSelection}
        />
      )}

      {/* Picked Video Preview & Caption Modal */}
      <ReactModal
        isOpen={!!pickedVideoUrl}
        onRequestClose={handlePickedVideoDiscard}
        className="flex h-screen w-screen items-center justify-center bg-black bg-opacity-80"
        overlayClassName="fixed inset-0 bg-black bg-opacity-80 z-50"
        ariaHideApp={false}
      >
        <div className="relative flex w-[90vw] max-w-[500px] flex-col items-center justify-center rounded-xl bg-blackishV2 p-6">
          <div
            className="absolute -right-3 -top-3 z-50 aspect-square cursor-pointer rounded-full bg-yellowish bg-opacity-80 p-2 text-xl text-white"
            onClick={() => {
              handlePickedVideoDiscard();
            }}
          >
            <IoMdClose />
          </div>

          <video
            id="picked-video-preview"
            src={pickedVideoUrl}
            controls
            className="w-full max-w-[500px] rounded-lg"
            style={{ maxHeight: "60vh" }}
          />
          <input
            type="text"
            value={pickedVideoCaption}
            onChange={(e) => setPickedVideoCaption(e.target.value)}
            placeholder="Add a caption (optional)"
            className="border-greyishV3 mt-4 w-11/12 rounded-md border bg-black px-3 py-2 text-white"
          />
          {/* Thumbnail controls */}
          <div className="mt-4 flex flex-col items-center gap-2">
            <div className="flex gap-3">
              <button
                className="rounded bg-blue-600 px-3 py-1 text-white"
                type="button"
                onClick={() =>
                  handleCaptureThumbnail(
                    document.getElementById("picked-video-preview"),
                  )
                }
              >
                Capture Thumbnail from Video
              </button>
              <label className="cursor-pointer rounded bg-blue-600 px-3 py-1 text-white">
                Pick Thumbnail
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleThumbnailFileChange}
                />
              </label>
            </div>
            {videoThumbnail && (
              <img
                src={videoThumbnail}
                alt="Video Thumbnail"
                className="border-greyishV3 mt-2 h-24 rounded border object-contain"
              />
            )}
          </div>
          <div className="mt-6 flex gap-4">
            <button
              className="flex items-center justify-center rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:scale-105"
              onClick={handlePickedVideoDiscard}
              aria-label="Delete"
            >
              <FaTrash size={18} />
            </button>
            <button
              className="flex items-center justify-center rounded-full bg-green-500 p-3 text-white shadow-lg transition hover:scale-105"
              onClick={handlePickedVideoSave}
              aria-label="Save"
            >
              <FaCheck size={18} />
            </button>
          </div>
        </div>
      </ReactModal>

      {/* Camera Modal for video recording */}
      <ReactModal
        isOpen={cameraModalOpen}
        onRequestClose={() => {
          handleDiscardVideo();
          setCameraModalOpen(false);
        }}
        className="flex h-screen w-screen items-center justify-center bg-black bg-opacity-80"
        overlayClassName="fixed inset-0 bg-black bg-opacity-80 z-50"
        ariaHideApp={false}
      >
        <div
          className={`relative flex flex-col items-center justify-center rounded-xl bg-blackishV2 ${videoRecorded ? "h-fit w-[40vw]" : "h-full w-[90vw] max-w-[1200px]"}`}
        >
          <div
            className={`absolute z-[10000] aspect-square cursor-pointer rounded-full bg-opacity-60 p-2 text-2xl text-white ${videoRecorded ? "-right-4 -top-4 bg-yellowish text-white" : "right-4 top-4 bg-black text-white"}`}
            onClick={() => {
              handleDiscardVideo();
              setCameraModalOpen(false);
            }}
          >
            <IoMdClose />
          </div>
          {!videoRecorded ? (
            <>
              <Webcam
                style={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "",
                }}
                audio={true}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  width: 1200,
                  height: 800,
                  facingMode: "user",
                }}
                mirrored={false}
                className="rounded-xl"
              />
              <div className="absolute left-4 top-4 flex items-center gap-2">
                {capturing && (
                  <>
                    <span className="h-3 w-3 animate-pulse rounded-full bg-red-500"></span>
                    <span className="font-mono text-lg text-white">
                      {formatTime(videoTimer)}
                    </span>
                  </>
                )}
              </div>
              <div className="absolute bottom-8 left-1/2 mt-6 flex -translate-x-1/2 gap-4">
                {!capturing && (
                  <button
                    className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-white bg-red-600 bg-opacity-80"
                    onClick={handleStartCaptureClick}
                  >
                    <FaStop size={20} className="invisible" />
                  </button>
                )}
                {capturing && (
                  <>
                    {!isPaused ? (
                      <button
                        className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-yellow-400 bg-yellow-600 bg-opacity-80"
                        onClick={handlePauseCapture}
                      >
                        <FaPause size={20} className="text-white" />
                      </button>
                    ) : (
                      <button
                        className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-yellow-400 bg-yellow-600 bg-opacity-80"
                        onClick={handleResumeCapture}
                      >
                        <FaPlay size={20} className="text-white" />
                      </button>
                    )}
                    <button
                      className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-red-500 bg-red-700 bg-opacity-80"
                      onClick={handleStopCaptureClick}
                    >
                      <FaStop size={20} className="text-white" />
                    </button>
                    <button
                      className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-gray-400 bg-gray-700 bg-opacity-80"
                      onClick={handleDiscardVideo}
                    >
                      <FaTrash size={20} className="text-white" />
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex w-full flex-col items-center justify-center p-6">
              <video
                id="recorded-video-preview"
                ref={setVideoPlayer}
                src={videoUrl}
                controls
                className="w-full rounded-lg"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
              <input
                type="text"
                value={videoCaption}
                onChange={(e) => setVideoCaption(e.target.value)}
                placeholder="Add a caption (optional)"
                className="border-greyishV3 mt-4 w-11/12 rounded-md border bg-black px-3 py-2 text-white"
              />
              {/* Thumbnail controls */}
              <div className="mt-4 flex flex-col items-center gap-2">
                <div className="flex gap-3">
                  <button
                    className="rounded bg-blue-600 px-3 py-1 text-white"
                    type="button"
                    onClick={() =>
                      handleCaptureThumbnail(
                        document.getElementById("recorded-video-preview"),
                      )
                    }
                  >
                    Capture Thumbnail from Video
                  </button>
                  <label className="cursor-pointer rounded bg-blue-600 px-3 py-1 text-white">
                    Pick Thumbnail
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleThumbnailFileChange}
                    />
                  </label>
                </div>
                {videoThumbnail && (
                  <img
                    src={videoThumbnail}
                    alt="Video Thumbnail"
                    className="border-greyishV3 mt-2 h-24 rounded border object-contain"
                  />
                )}
              </div>
              <div className="mb-10 mt-4 flex items-center gap-8">
                <button
                  className="flex items-center justify-center rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:scale-105"
                  onClick={handleDiscardVideo}
                  aria-label="Delete"
                >
                  <FaTrash size={18} />
                </button>
                <button
                  className="flex items-center justify-center rounded-full bg-green-500 p-3 text-white shadow-lg transition hover:scale-105"
                  onClick={handleVideoSave}
                  aria-label="Save"
                >
                  <FaCheck size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </ReactModal>
    </>
  );
};

export default UploadVideoButton;
